import * as React from 'react'
import { compose } from 'redux'
import { connect, Dispatch } from 'react-redux'
import { asyncValidate, shouldAsyncValidate } from 'redux-form-yup'
import { Field, reduxForm, InjectedFormProps } from 'redux-form'

import { Store } from '../../../../reducers/index'
import { Form as StyledForm } from '../../../Styled/Shared'
import ToggleView from '../ToggleView'
import UnitInput, { UnitConfig } from '../Inputs/UnitInput'
import { getActiveBeam, EditableBeam } from '../../../../reducers/project'

import { BeamSchema } from '../validationSchema'
import { Beam, BeamConcreteGrade, FireResistance } from '../formDataQueries'
import { updateBeam } from '../../../../actions/project'
import { handleKeyDown } from './shared'
import CustomSelect, { SelectProps } from '../../../Shared/CustomSelect'
import CustomRadio, { RadioProps } from '../../../Shared/CustomRadio'
import Tooltip, { Side } from '../../../Shared/Tooltip'
const tooltips = require('../../../Shared/tooltips.json')

import {
  formatMm,
  unitConfigConversion,
  parseMm,
  convertFireResistance,
  convertConcreteGrades,
  filterConcreteGrades,
  filterFireResistance
} from '../../../../location_changes/conversionHelper'

type ExternalProps = {
  beamTypes: Beam[]
  concreteGrades: BeamConcreteGrade[]
  onSubmit: any // tslint:disable-line
  fireResistances: FireResistance[]
}

type Props = InjectedFormProps<EditableBeam> & ExternalProps

type FormOption = {
  displayName: string
  id: string
}

type State = {
  tooltip: {
    pos: { x: number; y: number }
    id?: string
    show: boolean
    text: string
  }
}

class BeamFormView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      tooltip: { pos: { x: 0, y: 0 }, id: undefined, show: false, text: '' }
    }
    this.showTooltip = this.showTooltip.bind(this)
    this.hideTooltip = this.hideTooltip.bind(this)
  }

  parseOptions(options: FormOption[]) {
    return options.map((option, i) => (
      <option
        key={`${i}-${option}`}
        value={option.id}
        disabled={option.id === 'multiple-span'}
      >
        {convertConcreteGrades(convertFireResistance(option.displayName))}
      </option>
    ))
  }

  showTooltip(event: React.MouseEvent<HTMLElement>) {
    const id = event.currentTarget.getAttribute('id')
    const rect = event.currentTarget.getBoundingClientRect()
    let show = false
    let text = ''
    if (id === 'fireResistance') {
      show = true
      text = tooltips.fireResistance
    } else if (id === 'length') {
      show =
        this.props.initialValues.length !== undefined &&
        this.props.initialValues.length >= 12000
      text = tooltips.beamLength
    }
    this.setState({
      tooltip: {
        id: id ? id : undefined,
        pos: {
          x: rect.left,
          y: rect.top + rect.height / 2
        },
        show: show,
        text: text
      }
    })
  }

  hideTooltip() {
    this.setState({ tooltip: { ...this.state.tooltip, id: undefined } })
  }

  showLengthTip = (e: React.ChangeEvent<HTMLInputElement>) => {
    const show =
      !isNaN(parseInt(e.target.value, 10)) &&
      parseInt(e.target.value, 10) >= 12000
    this.setState({ tooltip: { ...this.state.tooltip, show: show } })
  }

  render() {
    const {
      handleSubmit,
      beamTypes,
      concreteGrades,
      fireResistances,
      initialValues
    } = this.props

    const currentType = initialValues.type
    const currentGrade = initialValues.concreteGrade
    const currentResistance = initialValues.fireResistance
    return (
      <StyledForm
        onKeyDown={handleKeyDown(handleSubmit)}
        onSubmit={handleSubmit}
      >
        <h4>Beam options</h4>
        <ToggleView title="Beam type">
          <label htmlFor="type">
            Type
            <Field
              name="type"
              component={CustomSelect}
              props={
                {
                  options: this.parseOptions(beamTypes),
                  currentVal: currentType
                } as SelectProps
              }
              autoFocus={true}
            />
          </label>
          <label htmlFor="profileType">
            Profile
            <div style={{ width: '170px', marginLeft: 'auto' }}>
              <div className="center-contents">
                <Field
                  name="profileType"
                  component={CustomRadio}
                  props={
                    {
                      isChecked: initialValues.profileType === 'D',
                      label: 'Slabs on both sides',
                      value: 'D'
                    } as RadioProps
                  }
                />
              </div>
              <div className="center-contents">
                <Field
                  name="profileType"
                  component={CustomRadio}
                  props={
                    {
                      isChecked: initialValues.profileType === 'DR',
                      label: 'Slab on one side',
                      value: 'DR'
                    } as RadioProps
                  }
                />
              </div>
            </div>
          </label>

          <label htmlFor="concreteGrade">
            Concrete grade
            <Field
              name="concreteGrade"
              component={CustomSelect}
              props={
                {
                  options: this.parseOptions(
                    filterConcreteGrades(concreteGrades)
                  ),
                  currentVal: currentGrade
                } as SelectProps
              }
            />
          </label>
          <div
            id="fireResistance"
            onMouseEnter={this.showTooltip}
            onMouseLeave={this.hideTooltip}
          >
            <label htmlFor="fireResistance">
              Fire resistance
              <Field
                name="fireResistance"
                component={CustomSelect}
                props={
                  {
                    options: this.parseOptions(
                      filterFireResistance(fireResistances)
                    ),
                    currentVal: currentResistance
                  } as SelectProps
                }
              />
            </label>
          </div>
        </ToggleView>
        <ToggleView title="Beam dimensions">
          <div
            id="length"
            onMouseEnter={this.showTooltip}
            onMouseLeave={this.hideTooltip}
          >
            <Field
              onChange={this.showLengthTip}
              required={true}
              name="length"
              component={UnitInput}
              parse={parseMm}
              format={formatMm}
              props={unitConfigConversion({
                title: 'Beam length',
                unit: 'mm'
              } as UnitConfig)}
            />
          </div>
        </ToggleView>
        <Tooltip
          tooltip={{
            pos: this.state.tooltip.pos,
            text: this.state.tooltip.text,
            side: Side.Left,
            show: this.state.tooltip.show && this.state.tooltip.id !== undefined
          }}
        />
      </StyledForm>
    )
  }
}

const mapStateToProps = (state: Store.Complete) => {
  const activeBeam = getActiveBeam(state)
  return {
    initialValues: { ...activeBeam, profileType: activeBeam.profileType || 'D' }
  }
}

// TODO: type this properly
export default compose(
  connect<{}, {}, ExternalProps>(mapStateToProps),
  reduxForm<Readonly<EditableBeam>>({
    form: 'beam',
    asyncValidate: asyncValidate(BeamSchema),
    shouldAsyncValidate,
    onChange(values: EditableBeam, dispatch: Dispatch<Store.Complete>) {
      dispatch(updateBeam(values))
    }
  })
)(BeamFormView)



// WEBPACK FOOTER //
// ./src/Components/Workspace/Calculation/Editors/BeamEditor.tsx